import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {AppComponent} from "../../app.component";
import {FS3ValidationResult} from "../../objects/validationResult";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {SharedDataService} from "../../services/shared-data.service";
import {DialogCanvasComponent} from "../dialog-canvas/dialog-canvas.component";
import {environment} from "../../../environments/environment";
import {FS3Notification} from "../../objects/notification";
import {AppEvent} from "../../objects/appEvent";
import {MatDatepickerInputEvent} from "@angular/material/datepicker";
import {FormatterInput} from "@fullcalendar/common";
import {DAY, hoursToSeconds} from "../../utils/parseUtils";
import {DateAdapter, MAT_DATE_LOCALE} from '@angular/material/core';
import {EventApi} from "@fullcalendar/angular";

//time-only variant of ScheduleCalendarEventFormComponent:
//no schedule type picker and no "duplicate changes" checkbox
@Component({
  selector: 'app-basic-calendar-event-form',
  templateUrl: './basic-calendar-event-form.component.html',
  styleUrls: ['../mobile/mobile-container/mobile-container.component.scss'],
  providers: [
    {provide: MAT_DATE_LOCALE, useValue: 'uk-UA'},
  ]
})
export class BasicCalendarEventFormComponent implements OnInit {
  @Input() application: AppComponent;
  @Input() payload: any;
  @Output() success = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  @ViewChild('eventStartInputElem') public eventStartInputElem: ElementRef<HTMLElement>;
  @ViewChild('eventEndInputElem') public eventEndInputElem: ElementRef<HTMLElement>;

  private formValidationMessages = {
    required: 'Value required.',
  };
  public validationResult: FS3ValidationResult;
  public componentForm: FormGroup = new FormGroup({});
  public eventStartInput: FormControl = new FormControl('', [Validators.required]);
  private eventStartValue: number;
  public eventHourStart: FormControl = new FormControl('', [Validators.required]);
  public eventEndInput: FormControl = new FormControl('', [Validators.required]);
  private eventEndValue: number;
  public eventHourEnd: FormControl = new FormControl('', [Validators.required]);
  public courtIDXControl: FormControl = new FormControl('', [Validators.required]);
  public calendarIDX = 0;
  public courts: object[] = [];

  state: string = 'edit';
  errMsg: string = '';
  public item: EventApi;
  public settings: object;
  public hours: string[]

  dateFormatter: FormatterInput = {
    month: 'long',
    day: 'numeric',
  }
  hourStart: string;
  minutesStart: string;
  hourEnd: string;
  minutesEnd: string;

  constructor(public dialog: MatDialog,
              private _adapter: DateAdapter<any>,
              private sharedDataService: SharedDataService) { }

  ngOnInit(): void {
    this.calendarIDX = this.payload['calendarIDX'] ?? 0;
    this.settings = {
      "start":AppComponent.convertHMtoSeconds(this.application.account.calendars[this.calendarIDX]['parsed_params']['start_hour']),
      "end":AppComponent.convertHMtoSeconds(this.application.account.calendars[this.calendarIDX]['parsed_params']['end_hour']),
      "duration":this.application.account.calendars[this.calendarIDX]['parsed_params']['duration']
    };
    this.hours = ((): string[] => {
      const settings = this.settings;
      let hours: string[] = []
      let start: number = settings['start'];
      let hour: number = 0;
      let minutes: number = 0;
      while(start <= settings['end']) {
        hour = Math.floor(start/3600);
        minutes = (start-hour*3600)/60;
        hours.push( ((hour < 10) ? '0'+hour : hour) + ':' + (minutes ? minutes : '00'));
        start+= settings['duration'];
      }
      return hours;
    })()

    this._adapter.setLocale('uk');
    this.item = this.payload['item'];
    this.componentForm.addControl('eventStartInput', this.eventStartInput);
    this.componentForm.addControl('eventHourStart', this.eventHourStart);
    this.componentForm.addControl('eventEndInput', this.eventEndInput);
    this.componentForm.addControl('eventHourEnd', this.eventHourEnd);
    this.componentForm.addControl('courtIDXControl', this.courtIDXControl);

    this.courts = this.application.account.calendars[this.calendarIDX]['parsed_params']['columns'];
    //grid resources come from settings.bookable_objects, courts from parsed_params.columns - same order, so the index matches either way
    const gridCourts = this.application.account.calendars[this.calendarIDX]['settings']['bookable_objects'] ?? this.courts;
    const pickedCourtIDX = gridCourts.findIndex(court => court['id'] == this.item?.extendedProps?.resourceId);
    this.courtIDXControl.setValue(pickedCourtIDX > -1 ? pickedCourtIDX : 0);

    if(this.item) {
      this.hourStart = (this.item.start.getHours() < 10) ? '0'+this.item.start.getHours() : this.item.start.getHours()+'';
      this.minutesStart = (this.item.start.getMinutes()) ? ''+this.item.start.getMinutes() : '00';
      this.hourEnd = (this.item.end.getHours() < 10) ? '0'+this.item.end.getHours() : this.item.end.getHours()+'';
      this.minutesEnd = (this.item.end.getMinutes()) ? ''+this.item.end.getMinutes() : '00';

      this.eventStartInput.setValue(this.item.start);
      this.eventStartValue = this.item.start.getTime()
      this.eventEndInput.setValue(this.item.end);
      this.eventEndValue = this.item.end.getTime()
      setTimeout(() => {
        this.eventStartInputElem.nativeElement['value'] = this.payload['calendarApi'].formatDate(this.item.start,this.dateFormatter);
        this.eventEndInputElem.nativeElement['value'] = this.payload['calendarApi'].formatDate(this.item.end,this.dateFormatter);
      })
      this.eventHourStart.setValue(this.hourStart+':'+this.minutesStart)
      this.eventHourEnd.setValue(this.hourEnd+':'+this.minutesEnd)
    } else { //create mode defaults to the day picked in the big calendar
      let pickedDay = new Date(this.payload['selectedDate'] ?? Date.now()); //copy, the caller keeps its own date
      pickedDay.setHours(0,0,0,0);
      this.eventStartInput.setValue(pickedDay);
      this.eventStartValue = pickedDay.getTime()
      this.eventEndInput.setValue(pickedDay);
      this.eventEndValue = pickedDay.getTime()
      setTimeout(() => {
        this.eventStartInputElem.nativeElement['value'] = this.payload['calendarApi'].formatDate(pickedDay,this.dateFormatter);
        this.eventEndInputElem.nativeElement['value'] = this.payload['calendarApi'].formatDate(pickedDay,this.dateFormatter);
      })
    }

    if(this.courtsDisabled) {
      this.courtIDXControl.disable();
    }
  }

  //editing: the event's own type decides; creating: the caller says what is being made
  public get isUnavailability(): boolean {
    return this.item
      ? this.item.extendedProps['schedule_type'] === 'unavailability'
      : this.payload['controlled_event_type'] === 'unavailability';
  }

  //an existing unavailability keeps its day, everything else can be moved
  public get datesDisabled(): boolean {
    return this.isUnavailability && !!this.item;
  }

  //the court an existing event sits on is not editable here
  public get courtsDisabled(): boolean {
    return !!this.item;
  }

  public dateInputClick(event,picker): void {
    event.currentTarget.blur()
    event.stopImmediatePropagation();
    picker.open();
  }
  dateChangeEvent(event: MatDatepickerInputEvent<Date>, isStart:boolean) {
    //any date can be picked, only start <= end is kept below
    event.targetElement['value'] = this.payload['calendarApi'].formatDate(event.value,this.dateFormatter);
    if(isStart) {
      this.eventStartValue = event.value.getTime()
      if(this.eventEndValue < this.eventStartValue || !this.eventEndValue) {
        this.eventEndValue = this.eventStartValue;
        this.eventEndInput.setValue(event.value);
        this.eventEndInputElem.nativeElement['value'] = event.targetElement['value'];
      }
    } else {
      this.eventEndValue = event.value.getTime()
      if(this.eventEndValue < this.eventStartValue || !this.eventStartValue) {
        this.eventStartValue = this.eventEndValue;
        this.eventStartInput.setValue(event.value);
        this.eventStartInputElem.nativeElement['value'] = event.targetElement['value'];
      }
    }
  }
  hourChange(isStart:boolean) {
    const start = hoursToSeconds(this.eventHourStart.value)
    const end = hoursToSeconds(this.eventHourEnd.value)
    if( start >= end || !start || !end) {
      isStart ?
        this.eventHourEnd.setValue(this.hours[this.hours.indexOf(this.eventHourStart.value)+1]) :
        this.eventHourStart.setValue(this.hours[this.hours.indexOf(this.eventHourEnd.value)-1]);
    }
  }

  public handleSubmit(event: Event | void = null): boolean {
    if (event) {
      event.stopPropagation();
    }

    this.validationResult = null;
    [this.eventStartInput,this.eventEndInput,this.eventHourStart,this.eventHourEnd].forEach(elem => {
      if (!elem.valid) {
        elem.markAsTouched()
        const errorKey = Object.keys(elem.errors)[0]; // get error key from input errors
        this.validationResult = {
          isValid: false,
          message: this.formValidationMessages[errorKey]
        };
      }
    });

    if (this.validationResult) {
      return false;
    }

    this.updateSchedule();
    return false
  }

  private updateSchedule(deleteItem: boolean = false, duplicateChanges: boolean = false) {
    this.state = 'loading';
    let configToSave;
    const label = this.payload?.controlled_event_type === 'unavailability' ? 'Add unavailability' : '';
    //'---' - used for now tu bypass booking_add submit that trigger usage updates
    const event_type = this.payload?.controlled_event_type !== 'unavailability' ? '---' : this.payload?.controlled_event_type;
    if(!deleteItem) {
      configToSave = {
        label,
        event_start_day: this.eventStartValue/1000,
        days_batch: (this.eventEndValue-this.eventStartValue)/DAY+1,
        event_hours: this.eventHourStart.value,
        event_seconds: hoursToSeconds(this.eventHourStart.value),
        duration: hoursToSeconds(this.eventHourEnd.value) - hoursToSeconds(this.eventHourStart.value),
        schedule_type: this.item?.extendedProps?.schedule_type ?? '', //form has no type picker, keep event type as is
        duplicate_changes: false,
        group_key: this.item ? this.item.extendedProps['group_key'] : '',
        calendar_id: this.application.account.calendars[this.calendarIDX]['ID'],
        court_object_idx: this.courtIDXControl.value,
      }
    } else {
      configToSave = {
        delete_item: true,
        duplicate_changes: duplicateChanges,
        group_key: this.item.extendedProps['group_key'],
      }
    }
    this.application.apiService
      .updateEventItem(
        environment['gid'],environment['g_hash'], '',
        this.payload['item'] ? this.payload['item']['id'] : null,
        'profile_event',event_type,configToSave)
      .subscribe(
        res => {
          let notification = new FS3Notification();
          notification.type = 'success';
          notification.text = AppComponent.getLabel('schedule_updated');
          this.sharedDataService.pushNotification(notification);
          if(!this.payload['item']) { //append new item to list
            this.drawCreatedEvents(res);
            //let appEvent = new AppEvent(AppEvent.SCHEDULE_ADDED, {bookingEvents: this.application.apiService.extractBookingData(res)});
            //this.sharedDataService.pushAppEvent(appEvent)
          } else {
            this.applyLocalChanges(deleteItem);
            //let appEvent = new AppEvent(AppEvent.SCHEDULE_UPDATED, {});
            //this.sharedDataService.pushAppEvent(appEvent)
          }
          this.success.emit()
        },
        error => {
          const { exception } = error;
          const errMsg = error['errMsg'] ? error['errMsg'] : (
            (exception && exception.error && exception.error.message) ? exception.error.message : 'Error'
          );
          let notification = new FS3Notification();
          notification.type = 'error';
          notification.text = errMsg;
          this.sharedDataService.pushNotification(notification);
          this.state = 'edit'
          this.validationResult = {
            isValid: false,
            message: errMsg
          };
        }
      )
  }

  //optimistic draw: put the created events into fullcalendar instead of reloading the whole grid
  private drawCreatedEvents(res): void {
    const calendarApi = this.payload['calendarApi'];
    if(!calendarApi) {
      return;
    }
    this.application.apiService.extractBookingData(res).forEach(bookingEvent => {
      if(!bookingEvent['resourceId']) { //resource view has no column to render it in
        console.warn('created event has no resourceId, it stays invisible on the grid', bookingEvent);
      }
      calendarApi.addEvent(bookingEvent);
    })
  }

  //optimistic update: move/remove the event in fullcalendar instead of reloading the whole grid
  private applyLocalChanges(deleteItem: boolean): void {
    if(!this.item) {
      return;
    }
    if(deleteItem) {
      this.item.remove();
      return;
    }
    let eventDay = new Date(this.eventStartValue);
    eventDay.setHours(0,0,0,0);
    this.item.setDates(
      new Date(eventDay.getTime() + hoursToSeconds(this.eventHourStart.value)*1000),
      new Date(eventDay.getTime() + hoursToSeconds(this.eventHourEnd.value)*1000)
    );
  }

  public onNoClick(): void {
    this.close.emit()
  }

  public deleteEvent(): void {
    const dialogRef = this.dialog.open(DialogCanvasComponent, {
      height: 'auto',
      data: {
        application: this,
        state: 'schedule-event-delete',
        payload: {
        }
      }
    } as MatDialogConfig);
    dialogRef.addPanelClass('common-dialog');
    dialogRef.addPanelClass('common-dialog-medium-form');
    dialogRef.afterOpened().subscribe(() => {
      (dialogRef.componentInstance as DialogCanvasComponent).isOpened = true;
    })
    dialogRef.afterClosed()
      .subscribe((dialogPayload) => {
        if(dialogPayload) {
          this.updateSchedule(true,dialogPayload['duplicateChanges'])
        }
      })
  }

}
